import { safeGet } from 'src/app/utilities/objects';

/**
 * @description
 * Make sure a list returned, either the original or a new empty list.
 *
 * @param list to be validated
 *
 * @return Returns either the input list or an empty list.
 */
export const safeList = (list): any[] => list || [];

export const isFieldNumeric = (list: any[], field: string): boolean => {
    return !(safeList(list).filter(i => i[field] && isNaN(i[field])).length > 0);
};

/**
 * @description
 * Sort a list by the specified field Ascending
 * Numbers will auto be sorted as numbers, and strings will be sorted as lowercase strings
 *
 * @param list that will be summed
 * @param field that should be sorted by
 *
 * @return Returns sorted list.
 */
export const sortAsc = (list: any[], field): any[] => {
  if (!field) { return safeList(list); }

  const isNumeric = isFieldNumeric(list, field);

  return safeList(list).sort( (a, b) => {
    const av = a[field] || '';
    const bv = b[field] || '';
    const aValue = isNumeric ? +av : av.toLowerCase();
    const bValue = isNumeric ? +bv : bv.toLowerCase();

    return aValue > bValue ? 1 : (aValue < bValue ? -1 : 0);
  });
};

/**
 * @description
 * Sort a list by the specified field Descending.
 * Numbers will auto be sorted as numbers, and strings will be sorted as lowercase strings
 *
 * @param list that will be summed
 * @param field that should be sorted by
 *
 * @return Returns sorted list.
 */
export const sortDesc = (list: any[], field): any[] => {
  if (!field) { return safeList(list); }

  const isNumeric = isFieldNumeric(list, field);

  return safeList(list).sort((a, b) => {
    const av = a[field] || '';
    const bv = b[field] || '';
    const aValue = isNumeric ? +av : av.toLowerCase();
    const bValue = isNumeric ? +bv : bv.toLowerCase();
    return aValue < bValue ? 1 : (aValue > bValue ? -1 : 0);
  });
};

/**
 * @description
 * sums a field from a list of objects
 *
 * @param list that will be summed
 * @param field that should be summed, can be either a single field 'value' or a object selector 'foo.bar.value'
 * @see safeGet
 *
 * @return Returns summed value.
 *
 * @example sumBy(list, 'value); sumBy(list, 'foo.bar.value');
 */
export const sumBy = (list: any[], field: string): number => {
  if (!field) { return 0; }
  return safeList(list).reduce( (count, item) => count + safeGet(field, item, 0) , 0 );
};

export const findByName = (list: any[], name: string) => {
  return safeList(list).find(item => item.name === name);
};

export const doDuplicatesExist = (list: any[], key: string, ignoreUndefined = false): boolean => {
  const lookup = {};
  let exists = false;
  safeList(list).forEach((item) => {
    if (lookup[item[key]]) {
      if (item[key] || !ignoreUndefined) {
        exists = true;
      }
      lookup[item[key]].push(item);
    } else {
      lookup[item[key]] = [item];
    }
  });

  return exists;
};

export const findDuplicates = (list: any[], key: string): {[key: string]: any[]} => {
  const lookup = {};

  safeList(list).forEach((item) => {
    if (lookup[item[key]]) {
      lookup[item[key]].push(item);
    } else {
      lookup[item[key]] = [item];
    }
  });

  Object.keys(lookup).forEach((k) => {
    if (lookup[k].length < 2) {
      delete lookup[k];
    }
  });

  return lookup;
};
